/**
 * Presentation for the Caffeine bundle page. The bundle engine owns state,
 * pricing and the cart add; this only reads what the engine rendered.
 *
 * - Pins the summary bar under the site header by measuring the sticky header.
 * - Drives the progress meter from the engine's count and size labels.
 * - Shows the bundle price against its subtotal, the saving, and whether the
 *   store's order discount will also apply in the cart.
 * - Splits card and summary titles into flavor, product line and size with
 *   CaffeineCartTitles (assets/caffeine-cart-titles.js).
 */
(function (global) {
  "use strict";

  function readInt(root, selector) {
    var node = root.querySelector(selector);
    var value = node ? parseInt(node.textContent, 10) : NaN;
    return isNaN(value) ? 0 : value;
  }

  // 0..1 progress plus a state name the CSS keys off.
  function meterState(count, size) {
    if (!size || size < 1) return { fill: 0, state: "empty" };
    var fill = Math.max(0, Math.min(1, count / size));
    return { fill: fill, state: fill >= 1 ? "complete" : fill > 0 ? "partial" : "empty" };
  }

  // A price the engine formatted with Intl (e.g. "$1,234.50" or "1.234,50 €"), back to a number.
  function parseMoney(text, locale) {
    var raw = String(text || "");
    if (!/\d/.test(raw)) return null;
    var decimal = ".";
    try {
      var part = new Intl.NumberFormat(locale || undefined).formatToParts(1.5).find(function (p) { return p.type === "decimal"; });
      if (part) decimal = part.value;
    } catch (e) {}
    var cleaned = raw.split(decimal).map(function (piece) { return piece.replace(/[^\d]/g, ""); });
    var value = Number(cleaned[0] + (cleaned.length > 1 ? "." + cleaned[1] : ""));
    return isNaN(value) ? null : value;
  }

  // What the bundle saves off its subtotal, as an amount and a whole percent; null when nothing is saved.
  function bundleSavings(subtotal, total) {
    if (subtotal == null || total == null || !(subtotal > total)) return null;
    var amount = Math.round((subtotal - total) * 100) / 100;
    return { amount: amount, percent: Math.round((amount / subtotal) * 100) };
  }

  function formatMoney(value, currency, locale) {
    try {
      return new Intl.NumberFormat(locale || undefined, { style: "currency", currency: currency }).format(value);
    } catch (e) {
      return value.toFixed(2);
    }
  }

  function stickyHeaderBottom(doc) {
    var view = doc.defaultView;
    var bottom = 0;
    doc.querySelectorAll(".shopify-section-group-header-group").forEach(function (section) {
      var position = view.getComputedStyle(section).position;
      if (position === "sticky" || position === "fixed") {
        bottom = Math.max(bottom, section.offsetHeight);
      }
    });
    return bottom;
  }

  function mount(root) {
    var doc = root.ownerDocument;
    var brand = root.getAttribute("data-title-brand") || "";
    var queued = false;

    function syncTop() {
      root.style.setProperty("--cbb-sticky-top", stickyHeaderBottom(doc) + "px");
    }

    function sync() {
      queued = false;
      var count = readInt(root, "[data-caffeine-bundle-count]");
      var size = readInt(root, "[data-caffeine-bundle-size-label]");
      var meter = meterState(count, size);
      root.style.setProperty("--cbb-fill", String(meter.fill));
      root.style.setProperty("--cbb-steps", String(Math.max(1, size)));
      root.setAttribute("data-cbb-state", meter.state);

      syncPrices();

      var titles = global.CaffeineCartTitles;
      if (!titles) return;
      root.querySelectorAll("[data-caffeine-bundle-title], [data-caffeine-bundle-slot-title]").forEach(function (el) {
        titles.splitInto(el, brand);
      });
    }

    function syncPrices() {
      var locale = root.getAttribute("data-locale");
      var currency = root.getAttribute("data-currency") || "USD";
      var subtotalNode = root.querySelector("[data-caffeine-bundle-subtotal]");
      var totalNode = root.querySelector("[data-caffeine-bundle-total]");
      var subtotal = parseMoney(subtotalNode && subtotalNode.textContent, locale);
      var total = parseMoney(totalNode && totalNode.textContent, locale);
      var savings = bundleSavings(subtotal, total);

      root.querySelectorAll("[data-cbb-was]").forEach(function (node) {
        node.hidden = !savings;
        var text = savings ? subtotalNode.textContent.trim() : "";
        if (node.textContent !== text) node.textContent = text;
      });
      var badge = root.querySelector("[data-cbb-savings]");
      if (badge) {
        badge.hidden = !savings;
        var amount = badge.querySelector("[data-cbb-savings-amount]");
        var percent = badge.querySelector("[data-cbb-savings-percent]");
        var amountText = savings ? formatMoney(savings.amount, currency, locale) : "";
        var percentText = savings ? " (" + savings.percent + "%)" : "";
        if (amount && amount.textContent !== amountText) amount.textContent = amountText;
        if (percent && percent.textContent !== percentText) percent.textContent = percentText;
      }
      // The store's order discount qualifies on the price after the bundle discount.
      var note = root.querySelector("[data-cbb-order-note]");
      var threshold = Number(root.getAttribute("data-order-threshold")) || 0;
      if (note) note.hidden = !(threshold > 0 && total != null && total >= threshold);
    }

    function schedule() {
      if (queued) return;
      queued = true;
      // Animation frames pause in hidden tabs; a timer keeps a backgrounded page current.
      var later = global.document && global.document.hidden ? global.setTimeout : (global.requestAnimationFrame || global.setTimeout);
      later(sync);
    }

    syncTop();
    sync();
    if (typeof global.MutationObserver === "function") {
      new global.MutationObserver(schedule).observe(root, { childList: true, subtree: true, characterData: true });
    }
    if (typeof global.ResizeObserver === "function") {
      doc.querySelectorAll(".shopify-section-group-header-group").forEach(function (section) {
        new global.ResizeObserver(syncTop).observe(section);
      });
    }
    global.addEventListener("resize", syncTop);
  }

  var api = { meterState: meterState, parseMoney: parseMoney, bundleSavings: bundleSavings };
  if (typeof module === "object" && module.exports) {
    module.exports = api;
    return;
  }
  function start() {
    global.document.querySelectorAll("[data-caffeine-bundle]").forEach(mount);
  }
  if (global.document.readyState === "loading") {
    global.document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})(typeof window !== "undefined" ? window : globalThis);
